/**
 * CommandPalette — CMD+K global command surface.
 *
 * Features:
 *   - Instant client-side search via localSearch() on cached my-tasks data
 *   - Cross-board API search via useCrossBoardSearch(), re-ranked by rankResults()
 *   - Match highlighting via highlight() — renders <mark> tags in result labels
 *   - Fuzzy match for app commands and notes
 *   - Keyboard navigation (↑ ↓ Enter Esc)
 *   - Result grouping: Commands | Items | Boards | Notes | Actions
 *   - Debounced API search (200 ms default staleTime gate)
 *
 * Usage:
 *   Mount <CommandPalette /> at app root level.  It registers a keydown
 *   listener on document and manages its own open/closed state.  The global
 *   Zustand flag `commandPaletteOpen` lets other parts of the UI open it
 *   programmatically.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Loader } from 'monday-ui-react-core';
import { useAppStore } from '@core/store/appStore';
import { useMyTasks, useCrossBoardSearch } from '@core/hooks/useMondayData';
import { localSearch, rankResults, highlight } from '@core/intelligence';
import { useVault } from '@core/hooks/useVault';
import type { SearchResult } from '@core/types';

// ─── App Commands ─────────────────────────────────────────────────────────────

interface AppCommand {
  id: string;
  label: string;
  description: string;
  icon: string;
  action: () => void;
}

function useAppCommands(): AppCommand[] {
  const { setActiveTab, setEditMode } = useAppStore();

  return [
    { id: 'go_home',      label: 'Go to Home',      description: 'Open the Home tab',      icon: '🏠', action: () => setActiveTab('home') },
    { id: 'go_mywork',    label: 'Go to My Work',   description: 'Open the My Work tab',   icon: '🎯', action: () => setActiveTab('my_work') },
    { id: 'go_inbox',     label: 'Go to Inbox',     description: 'Open the Inbox tab',     icon: '📬', action: () => setActiveTab('inbox') },
    { id: 'go_vault',     label: 'Go to Vault',     description: 'Open the Vault tab',     icon: '🗃️', action: () => setActiveTab('vault') },
    { id: 'go_monitor',   label: 'Go to Monitor',   description: 'Open the Monitor tab',   icon: '📡', action: () => setActiveTab('monitor') },
    { id: 'go_calendar',  label: 'Go to Calendar',  description: 'Open the Calendar tab',  icon: '📅', action: () => setActiveTab('calendar') },
    { id: 'go_analytics', label: 'Go to Analytics', description: 'Open the Analytics tab', icon: '📊', action: () => setActiveTab('analytics') },
    { id: 'go_admin',     label: 'Go to Admin',     description: 'Open the Admin panel',   icon: '🔧', action: () => setActiveTab('admin') },
    { id: 'go_settings',  label: 'Go to Settings',  description: 'Open the Settings tab',  icon: '⚙️', action: () => setActiveTab('settings') },
    { id: 'edit_mode',    label: 'Edit Layout',     description: 'Toggle page edit mode',  icon: '✏️', action: () => setEditMode(true) },
  ];
}

// ─── Result Types ─────────────────────────────────────────────────────────────

type ResultGroup = 'command' | 'board' | 'item' | 'note' | 'action';

interface PaletteResult {
  id: string;
  group: ResultGroup;
  /** Raw label text — used for keyboard nav and aria-labels */
  label: string;
  /** HTML string with <mark> tags for highlighted matches */
  labelHtml: string;
  subtitle?: string;
  icon: string;
  onSelect: () => void;
}

const GROUP_ORDER: ResultGroup[] = ['command', 'item', 'board', 'note', 'action'];
const GROUP_LABELS: Record<ResultGroup, string> = {
  command: 'Commands',
  item:    'Items',
  board:   'Boards',
  note:    'Notes',
  action:  'Actions',
};

// ─── Fuzzy match (commands / notes) ──────────────────────────────────────────

function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

// ─── Keyboard hook ────────────────────────────────────────────────────────────

function useKeyboardNav(
  results: PaletteResult[],
  activeIndex: number,
  setActiveIndex: Dispatch<SetStateAction<number>>,
  onSelect: (r: PaletteResult) => void,
  onClose: () => void,
) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && results[activeIndex]) {
        e.preventDefault();
        onSelect(results[activeIndex]);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [results, activeIndex, setActiveIndex, onSelect, onClose]);
}

// ─── Individual Result Row ────────────────────────────────────────────────────

interface ResultRowProps {
  result: PaletteResult;
  active: boolean;
  onHover: () => void;
  onSelect: () => void;
}

function ResultRow({ result, active, onHover, onSelect }: ResultRowProps) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (active && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  return (
    <button
      ref={ref}
      className={`cp-result${active ? ' cp-result--active' : ''}`}
      onMouseEnter={onHover}
      onClick={onSelect}
      type="button"
      aria-selected={active}
    >
      <span className="cp-result__icon" aria-hidden="true">
        {result.icon}
      </span>
      <span className="cp-result__text">
        {/* labelHtml contains <mark> tags from highlight() — safe: generated by our own code */}
        <span
          className="cp-result__label"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: result.labelHtml }}
        />
        {result.subtitle && (
          <span className="cp-result__subtitle">{result.subtitle}</span>
        )}
      </span>
    </button>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function CommandPalette() {
  const { commandPaletteOpen, setCommandPaletteOpen, setActiveTab } = useAppStore();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const appCommands = useAppCommands();
  const { notes, actions: vaultActions } = useVault();

  // My-tasks data — already warm in the React Query cache from the My Work tab.
  // We use it to power instant local search without a round-trip.
  const { data: myTasks = [] } = useMyTasks(50);

  // Cross-board API search — gated at ≥ 2 chars like the previous version.
  const { data: apiResults = [], isFetching: searching } = useCrossBoardSearch(query, 20);

  // Open via CMD+K / CTRL+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setCommandPaletteOpen]);

  // Focus input when palette opens
  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandPaletteOpen]);

  // Build the flat result list
  const results: PaletteResult[] = useMemo(() => {
    const list: PaletteResult[] = [];
    const q = query.trim();

    // ── Commands — fuzzy match ───────────────────────────────────────────────
    appCommands
      .filter((c) => !q || fuzzyMatch(q, c.label) || fuzzyMatch(q, c.description))
      .forEach((c) =>
        list.push({
          id:       `cmd_${c.id}`,
          group:    'command',
          label:    c.label,
          labelHtml: q ? highlight(c.label, q) : c.label,
          subtitle: c.description,
          icon:     c.icon,
          onSelect: c.action,
        }),
      );

    // ── Local item search — instant, no spinner ───────────────────────────────
    if (q.length >= 2 && myTasks.length > 0) {
      const localHits = localSearch(myTasks, q, 5);
      localHits.forEach((r) =>
        list.push({
          id:       `local_${r.id}`,
          group:    'item',
          label:    r.name,
          labelHtml: highlight(r.name, q),
          subtitle: r.boardName ?? undefined,
          icon:     '📌',
          onSelect: () => setActiveTab('my_work'),
        }),
      );
    }

    // ── API search results — re-ranked ────────────────────────────────────────
    if (q.length >= 2 && apiResults.length > 0) {
      const ranked = rankResults(apiResults as unknown as Parameters<typeof rankResults>[0], q);
      // De-duplicate against local hits (same item may appear in both)
      const localIds = new Set(
        list.filter((r) => r.group === 'item').map((r) => r.id.replace('local_', '')),
      );
      ranked.forEach((r: SearchResult) => {
        if (localIds.has(String(r.id))) return;
        list.push({
          id:       `sr_${r.id}`,
          group:    r.type === 'board' ? 'board' : 'item',
          label:    r.name,
          labelHtml: highlight(r.name, q),
          subtitle: r.boardName ?? undefined,
          icon:     r.type === 'board' ? '📋' : '📌',
          onSelect: () => {
            if (r.type === 'board') {
              window.open(`https://monday.com/boards/${r.id}`, '_blank', 'noopener');
            } else {
              window.open(
                `https://monday.com/boards/${r.boardId}/pulses/${r.id}`,
                '_blank',
                'noopener',
              );
            }
          },
        });
      });
    }

    // ── Notes ────────────────────────────────────────────────────────────────
    if (!q || q.length >= 2) {
      notes
        .filter((n) => !q || fuzzyMatch(q, n.name) || fuzzyMatch(q, n.content ?? ''))
        .slice(0, 5)
        .forEach((n) =>
          list.push({
            id:       `note_${n.id}`,
            group:    'note',
            label:    n.name,
            labelHtml: q ? highlight(n.name, q) : n.name,
            subtitle: n.content ? n.content.slice(0, 60) : undefined,
            icon:     '📝',
            onSelect: () => setActiveTab('vault'),
          }),
        );
    }

    // ── Actions ──────────────────────────────────────────────────────────────
    if (!q || q.length >= 2) {
      vaultActions
        .filter((a) => !q || fuzzyMatch(q, a.name))
        .slice(0, 5)
        .forEach((a) =>
          list.push({
            id:       `action_${a.id}`,
            group:    'action',
            label:    a.name,
            labelHtml: q ? highlight(a.name, q) : a.name,
            subtitle: a.status,
            icon:     '⚡',
            onSelect: () => setActiveTab('my_work'),
          }),
        );
    }

    return list;
  }, [query, appCommands, myTasks, apiResults, notes, vaultActions, setActiveTab]);

  const handleClose = useCallback(() => {
    setCommandPaletteOpen(false);
    setQuery('');
  }, [setCommandPaletteOpen]);

  const handleSelect = useCallback(
    (result: PaletteResult) => {
      result.onSelect();
      handleClose();
    },
    [handleClose],
  );

  useKeyboardNav(results, activeIndex, setActiveIndex, handleSelect, handleClose);

  if (!commandPaletteOpen) return null;

  // Group results for display
  const grouped: Partial<Record<ResultGroup, PaletteResult[]>> = {};
  results.forEach((r) => {
    if (!grouped[r.group]) grouped[r.group] = [];
    grouped[r.group]!.push(r);
  });

  // Flat ordered list for keyboard index calculation
  const flat = GROUP_ORDER.flatMap((g) => grouped[g] ?? []);
  const flatIndex = (result: PaletteResult) => flat.findIndex((r) => r.id === result.id);

  return (
    <div
      className="cp-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Command Palette"
    >
      <div className="cp-modal">
        {/* Search input */}
        <div className="cp-search">
          <span className="cp-search__icon" aria-hidden="true">🔍</span>
          <input
            ref={inputRef}
            className="cp-search__input"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            placeholder="Search boards, items, notes, commands…"
            aria-label="Search"
            autoComplete="off"
            spellCheck={false}
          />
          {searching && (
            <Loader size={Loader.sizes.XS} className="cp-search__loader" />
          )}
          <kbd className="cp-search__esc" aria-label="Press Escape to close">Esc</kbd>
        </div>

        {/* Results */}
        <div className="cp-results" role="listbox" aria-label="Search results">
          {flat.length === 0 && !searching ? (
            <p className="cp-empty">
              {query ? `No results for "${query}"` : 'Type to search or press a command…'}
            </p>
          ) : (
            GROUP_ORDER.map((group) => {
              const items = grouped[group];
              if (!items || items.length === 0) return null;
              return (
                <div key={group} className="cp-group" role="group" aria-label={GROUP_LABELS[group]}>
                  <div className="cp-group__label" aria-hidden="true">
                    {GROUP_LABELS[group]}
                  </div>
                  {items.map((r) => {
                    const idx = flatIndex(r);
                    return (
                      <ResultRow
                        key={r.id}
                        result={r}
                        active={idx === activeIndex}
                        onHover={() => setActiveIndex(idx)}
                        onSelect={() => handleSelect(r)}
                      />
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="cp-footer" aria-hidden="true">
          <span><kbd>↑ ↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Select</span>
          <span><kbd>Esc</kbd> Dismiss</span>
        </div>
      </div>
    </div>
  );
}
